import { StatusBadge } from "@/components/StatusBadge";
import type { QuestListRow } from "@shared/dto";
import styles from "./RungPanel.module.css";

const numberFormatter = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 3,
});

// numeric(12,3) arrives as "10000.000", which reads wrong and would also shift
// the plural category if it ever reached a title.
const formatTarget = (value: string | null): string =>
  value === null ? "—" : numberFormatter.format(Number(value));

type RungPanelProps = {
  rungs: QuestListRow[];
};

export const RungPanel = ({ rungs }: RungPanelProps) => {
  if (rungs.length === 0) {
    // Not decoration: a published template with no active rung is unofferable
    // and invisible, which is one of the twelve publish rules.
    return (
      <p className={styles.empty}>
        No rungs yet — this template cannot be published.
      </p>
    );
  }

  return (
    <table className={styles.table}>
      <caption className={styles.caption}>Rungs</caption>
      <thead>
        <tr>
          <th scope="col" className={styles.header}>
            Assignment
          </th>
          <th scope="col" className={styles.header}>
            Target
          </th>
          <th scope="col" className={styles.header}>
            Days
          </th>
          <th scope="col" className={styles.header}>
            Difficulty
          </th>
          <th scope="col" className={styles.header}>
            Effort
          </th>
          <th scope="col" className={styles.header}>
            Status
          </th>
        </tr>
      </thead>
      <tbody>
        {rungs.map((rung) => (
          // Archived rungs stay listed rather than hidden: the ladder uniques
          // only see active ones, so an author has to know what is parked
          // before reusing a difficulty or a target.
          <tr
            key={rung.id}
            data-archived={rung.status !== "ACTIVE" || undefined}
          >
            {/* The title is the template's own and already heads the row this
                panel hangs under, so only the assignment is repeated here. */}
            <th scope="row" className={styles.title}>
              {rung.assignment ?? "—"}
            </th>
            <td className={styles.cell}>{formatTarget(rung.targetValue)}</td>
            <td className={styles.cell}>{rung.requiredDays ?? "—"}</td>
            <td className={styles.cell}>{rung.difficultyLevel}</td>
            <td className={styles.cell}>{rung.effort}</td>
            <td className={styles.cell}>
              <StatusBadge
                label={rung.status}
                tone={rung.status === "ACTIVE" ? "accent" : "neutral"}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
